import { HTMLAttributes, RefAttributes } from 'react'

import { NextLink } from '../../../app/_components/navigation/NextLink'
import { useGenerateFilterSelectionPath } from '../../../brunswick/components/pages/Collection/sections/CollectionGridSection/hooks/useGenerateFilterSelectionPath'
import { useCollectionFilterQueryParamsContext } from '../../../brunswick/containers/CollectionFilterQueryParamsContext'
import { CollectionItemFilterValue } from '../../../types/graphql/collection-view'

import * as Styles from './styles'

export type CheckboxFilterItemProps = {
  /* The unique key of the filter group. */
  filterKey: string
  /* The filter value object. */
  filterValue: CollectionItemFilterValue
  /* Render the menu item as an anchor instead of an interactive button. */
  asLink?: boolean
  /* Whether the checkbox should display it's hover state. */
  hover?: boolean
} & HTMLAttributes<HTMLInputElement> &
  RefAttributes<HTMLInputElement>

/* A basic checkbox menu item to display within a list of filters. */
export function CheckboxFilterItem({
  filterValue,
  filterKey,
  asLink,
  hover,
  onChange,
  ref,
  ...rest
}: CheckboxFilterItemProps) {
  const { selectedFilters, toggleSelectedFilter } = useCollectionFilterQueryParamsContext()

  const generateFilterSelectionPath = useGenerateFilterSelectionPath()

  const selectedValues = selectedFilters[filterKey]
  const key = `${filterKey}:${filterValue.value}`
  const path = generateFilterSelectionPath(filterKey, filterValue.value)

  const handleChange = (value: string) => {
    if (asLink) return // When filter selectors are rendered as links, the page navigation will handle updating the query params.
    toggleSelectedFilter(filterKey, value)
  }

  const selector = (
    <Styles.OptionCheckbox
      ref={ref}
      key={key}
      // Optimistically display selected state while network request resolves:
      checked={filterValue.state === 'SELECTED' || !!selectedValues?.includes(filterValue.value)}
      disabled={filterValue.state === 'DISABLED'}
      hover={hover}
      type='checkbox'
      value={filterValue.value}
      onChange={evt => {
        handleChange(filterValue.value)
        onChange?.(evt)
      }}
      {...rest}
    >
      <Styles.Label>{filterValue.displayValue}</Styles.Label>
    </Styles.OptionCheckbox>
  )
  return asLink ? (
    <NextLink href={path} shallow={true} tabIndex={-1}>
      {selector}
    </NextLink>
  ) : (
    selector
  )
}
